import {
  activityPayloadFromBaseline,
  activityPayloadFromLesson,
  emptyLifetimeStats,
  estimateKeystrokesFromWords,
  normalizeDailySummary,
  type ActivityPayload,
} from './activityLog';
import {
  localDateFromIso,
  mergeLessonIntoDailySummary,
  xpForLesson,
} from './stats';
import type {
  ActivityKind,
  ActivityLogEntry,
  DailySummary,
  LifetimeStats,
  UserProfile,
} from '../types';

function mergePayloadIntoLifetime(
  stats: LifetimeStats,
  payload: ActivityPayload,
): LifetimeStats {
  const activityByKind = { ...stats.activityByKind };
  activityByKind[payload.kind] = (activityByKind[payload.kind] ?? 0) + 1;
  return {
    totalKeystrokes: stats.totalKeystrokes + payload.totalKeystrokes,
    correctChars: stats.correctChars + payload.correctChars,
    sessionCount: stats.sessionCount + 1,
    activityByKind,
  };
}

function payloadsFromUser(user: UserProfile): ActivityPayload[] {
  const out: ActivityPayload[] = [];

  for (const r of user.recentResults) {
    out.push(activityPayloadFromLesson(r, xpForLesson(r)));
  }

  if (user.baselineTranscription) {
    out.push(activityPayloadFromBaseline(user.baselineTranscription));
  }
  if (user.baselineComposition) {
    out.push(activityPayloadFromBaseline(user.baselineComposition));
  }

  for (const c of user.wpmCheckIns ?? []) {
    out.push({
      kind: 'wpm-check-in',
      at: c.takenAt,
      durationMs: c.durationMs,
      totalKeystrokes: c.totalKeystrokes,
      correctChars: c.correctChars,
      wpm: c.wpm,
      accuracy: c.accuracy,
    });
  }

  for (const [trinId, rec] of Object.entries(user.trinProofRecords ?? {})) {
    if (!rec.lastPassedAt) continue;
    out.push({
      kind: 'trin-proof',
      at: rec.lastPassedAt,
      durationMs: 0,
      totalKeystrokes: 0,
      correctChars: 0,
      trinId,
    });
  }

  for (const [checkpointId, rec] of Object.entries(
    user.miniCheckpointRecords ?? {},
  )) {
    const est = estimateKeystrokesFromWords(rec.bestWords);
    out.push({
      kind: 'mini-checkpoint',
      at: rec.lastPlayedAt,
      durationMs: 0,
      totalKeystrokes: est.totalKeystrokes,
      correctChars: est.correctChars,
      checkpointId,
    });
  }

  for (const [checkpointId, rec] of Object.entries(
    user.checkpointRecords ?? {},
  )) {
    const est = estimateKeystrokesFromWords(rec.bestWords);
    out.push({
      kind: 'checkpoint-sprint',
      at: rec.lastPlayedAt,
      durationMs: 0,
      totalKeystrokes: est.totalKeystrokes,
      correctChars: est.correctChars,
      checkpointId,
    });
  }

  return out;
}

function backfillDailyActivityFields(
  summaries: Record<string, DailySummary>,
): Record<string, DailySummary> {
  const out: Record<string, DailySummary> = {};
  for (const [date, raw] of Object.entries(summaries)) {
    const s = normalizeDailySummary({ ...raw, date });
    if (s.totalKeystrokes > 0 || s.sessionCount > 0) {
      out[date] = s;
      continue;
    }
    out[date] = {
      ...s,
      sessionCount: Math.max(s.sessionCount, s.lessonCount),
    };
  }
  return out;
}

function buildLifetimeFromDaily(
  summaries: Record<string, DailySummary>,
): LifetimeStats {
  let stats = emptyLifetimeStats();
  for (const raw of Object.values(summaries)) {
    const s = normalizeDailySummary(raw);
    stats.totalKeystrokes += s.totalKeystrokes;
    stats.correctChars += s.correctChars;
    stats.sessionCount += s.sessionCount;
    for (const [kind, count] of Object.entries(s.activityByKind)) {
      const k = kind as ActivityKind;
      stats.activityByKind[k] = (stats.activityByKind[k] ?? 0) + (count ?? 0);
    }
  }
  return stats;
}

function buildActivityLogFromRecent(
  user: UserProfile,
): ActivityLogEntry[] {
  const existing = user.activityLog ?? [];
  if (existing.length > 0) return existing;

  return user.recentResults.slice(0, 30).map((r) => {
    const payload = activityPayloadFromLesson(r, xpForLesson(r));
    return {
      id: `${r.startedAt}-${payload.kind}`,
      kind: payload.kind,
      at: payload.at,
      durationMs: payload.durationMs,
      totalKeystrokes: payload.totalKeystrokes,
      correctChars: payload.correctChars,
      wpm: payload.wpm,
      accuracy: payload.accuracy,
      trinId: payload.trinId,
      xpGained: payload.xpGained,
    };
  });
}

/** Udfyld tastetælling, aktivitetslog og livstidsstat fra eksisterende data. */
export function migrateUserToV34(user: UserProfile): UserProfile {
  if (user.lifetimeStats && (user.activityLog?.length ?? 0) > 0) {
    return {
      ...user,
      dailySummaries: backfillDailyActivityFields(user.dailySummaries ?? {}),
      lifetimeStats: user.lifetimeStats,
    };
  }

  let dailySummaries = backfillDailyActivityFields(user.dailySummaries ?? {});
  const hasKeystrokeData = Object.values(dailySummaries).some(
    (s) => (normalizeDailySummary(s).totalKeystrokes ?? 0) > 0,
  );
  if (!hasKeystrokeData && user.recentResults.length > 0) {
    for (const r of user.recentResults) {
      const date = localDateFromIso(r.startedAt);
      dailySummaries[date] = mergeLessonIntoDailySummary(
        dailySummaries[date],
        r,
        date,
        xpForLesson(r),
      );
    }
  }

  let lifetimeStats = user.lifetimeStats ?? emptyLifetimeStats();

  if (lifetimeStats.sessionCount === 0) {
    const fromDaily = buildLifetimeFromDaily(dailySummaries);
    if (fromDaily.totalKeystrokes > 0 || fromDaily.sessionCount > 0) {
      lifetimeStats = fromDaily;
    } else {
      for (const payload of payloadsFromUser(user)) {
        lifetimeStats = mergePayloadIntoLifetime(lifetimeStats, payload);
      }
    }
  }

  const activityLog = buildActivityLogFromRecent(user);

  return {
    ...user,
    dailySummaries,
    lifetimeStats,
    activityLog,
  };
}
